import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Segment, Header, Button, Form, Checkbox, Confirm, Label, Message, Dropdown } from "semantic-ui-react";
import format from "date-fns/format";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { compareText, groupsOf, normaliseGroup } from "./appUserGroups";

import firebase from "../../../app/config/firebase";
const firestore = firebase.firestore();

// Lock screens cut messages off at roughly this length
const LOCK_SCREEN_CHARS = 150;

// Same people the sendBookingNotification function accepts: the main client plus app users
// with a login. Seeded dummy users and users without an app login have no appUserUid.
function bookingRecipients(client, clientUsers) {
    const recipients = new Map();
    if (client?.appUserUid) {
        recipients.set(client.appUserUid, { name: client.client_name || client.client_email || "Main client", group: "" });
    }
    (clientUsers || []).forEach((user) => {
        if (user.appUserUid && !recipients.has(user.appUserUid)) {
            recipients.set(user.appUserUid, { name: user.client_name || user.client_email || "App user", group: normaliseGroup(user.group) });
        }
    });
    // Grouped travellers listed by group, so a group's members sit together in a long list
    return [...recipients]
        .map(([uid, details]) => ({ uid, ...details }))
        .sort((a, b) => (!a.group !== !b.group ? (a.group ? -1 : 1) : compareText(a.group, b.group) || compareText(a.name, b.name)));
}

export default function AppNotifications({ bookingId, client, clientUsers }) {
    const recipients = useMemo(() => bookingRecipients(client, clientUsers), [client, clientUsers]);
    const recipientKey = recipients.map((r) => r.uid).join(",");
    const nameFor = useCallback((uid) => recipients.find((r) => r.uid === uid)?.name || "Removed user", [recipients]);
    const groups = useMemo(() => groupsOf(recipients), [recipients]);

    const [message, setMessage] = useState("");
    const [selected, setSelected] = useState([]);
    const [canReceive, setCanReceive] = useState({});
    const [sending, setSending] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const { currentUserProfile } = useSelector((state) => state.profile);
    const [sentMessages, setSentMessages] = useState([]);

    // Everyone is ticked by default; re-tick when the booking's users change
    useEffect(() => {
        setSelected(recipients.map((r) => r.uid));
    }, [recipientKey]);

    const loadDeviceStatus = useCallback(async () => {
        const entries = await Promise.all(
            recipients.map(async (r) => {
                const snap = await firestore.collection("users").doc(r.uid).get();
                const tokens = snap.exists ? snap.data().pushTokens : null;
                return [r.uid, Array.isArray(tokens) && tokens.length > 0];
            }),
        );
        setCanReceive(Object.fromEntries(entries));
    }, [recipientKey]);

    useEffect(() => {
        loadDeviceStatus().catch((error) => console.log("Device status check failed:", error));
    }, [loadDeviceStatus]);

    useEffect(() => {
        const unsubscribe = firestore
            .collection("bookings")
            .doc(bookingId)
            .collection("notifications")
            .orderBy("createdAt", "desc")
            .onSnapshot((snapshot) => {
                setSentMessages(snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
            });
        return () => unsubscribe();
    }, [bookingId]);

    function toggle(uid) {
        setSelected((current) => (current.includes(uid) ? current.filter((id) => id !== uid) : [...current, uid]));
    }

    // Removes the message from the booking (and so from every traveller's Messages tab, on any app
    // version), keeping a copy with who deleted it in deleted_notifications as a record
    async function deleteSent() {
        const target = deleteTarget;
        setDeleteTarget(null);
        try {
            const notifications = firestore.collection("bookings").doc(bookingId).collection("notifications");
            const { id, ...data } = target;
            const batch = firestore.batch();
            batch.set(firestore.collection("bookings").doc(bookingId).collection("deleted_notifications").doc(id), {
                ...data,
                deletedAt: firebase.firestore.FieldValue.serverTimestamp(),
                deletedByUid: currentUserProfile?.id || "",
                deletedByName: currentUserProfile?.displayName || "",
            });
            batch.delete(notifications.doc(id));
            await batch.commit();
            toast.success("Message deleted");
        } catch (error) {
            console.log(error);
            toast.error("Could not delete the message");
        }
    }

    async function send() {
        setConfirmOpen(false);
        setSending(true);
        try {
            const sendBookingNotification = firebase.app().functions("europe-west2").httpsCallable("sendBookingNotification");
            const result = await sendBookingNotification({ bookingId, message: message.trim(), recipientUids: selected });
            const { sentCount, recipientCount, noTokenUids } = result.data;

            toast.success(`Notification sent to ${sentCount} of ${recipientCount}`);
            if (noTokenUids.length > 0) {
                toast.warning(`Could not reach: ${noTokenUids.map(nameFor).join(", ")}. Please contact them another way.`, { autoClose: false });
            }
            setMessage("");
            loadDeviceStatus().catch(() => {});
        } catch (error) {
            console.log(error);
            toast.error(error.message || "Could not send the notification");
        } finally {
            setSending(false);
        }
    }

    const unreachable = recipients.filter((r) => canReceive[r.uid] === false);
    const selectedCount = selected.filter((uid) => recipients.some((r) => r.uid === uid)).length;
    const trimmed = message.trim();

    return (
        <>
            <Segment attached="top" style={{ marginTop: "1em" }}>
                <Header as="h3">Push Notifications (Message everyone on this booking through the App)</Header>
            </Segment>

            <Segment attached>
                {recipients.length === 0 ? (
                    "No app users with a login on this booking yet"
                ) : (
                    <Form>
                        <Form.TextArea
                            label="Message"
                            placeholder="e.g. Please will group 1 meet at the lobby for transfer by 08:30"
                            value={message}
                            onChange={(e, { value }) => setMessage(value)}
                            rows={3}
                        />
                        <p style={{ marginTop: -8, color: trimmed.length > LOCK_SCREEN_CHARS ? "#db2828" : "#888" }}>
                            {trimmed.length} characters
                            {trimmed.length > LOCK_SCREEN_CHARS && " (longer messages are cut off on lock screens; travellers can read the rest in the App)"}
                        </p>

                        <Form.Field>
                            {/* Buttons must sit outside the label: a click anywhere inside a label is forwarded to
                                the first button in it, which made the group dropdown trigger Select none */}
                            <label>
                                Send to ({selectedCount} of {recipients.length})
                            </label>
                            <div style={{ marginBottom: 10 }}>
                                <Button
                                    basic
                                    size="mini"
                                    type="button"
                                    onClick={() => setSelected(selectedCount === recipients.length ? [] : recipients.map((r) => r.uid))}
                                >
                                    {selectedCount === recipients.length ? "Select none" : "Select all"}
                                </Button>
                                {groups.length > 0 && (
                                    <Dropdown
                                        button
                                        basic
                                        className="mini"
                                        text="Select group"
                                        options={groups.map((group) => ({ key: group, value: group, text: group }))}
                                        onChange={(e, { value }) =>
                                            setSelected(recipients.filter((r) => r.group.toLowerCase() === value.toLowerCase()).map((r) => r.uid))
                                        }
                                        value={null}
                                    />
                                )}
                            </div>
                            {/* Scrolling box, so a 200-person group doesn't stretch the page */}
                            <div
                                style={{
                                    maxHeight: 320,
                                    overflowY: "auto",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    padding: "8px 10px",
                                }}
                            >
                                {recipients.map((r) => (
                                    <div key={r.uid} style={{ marginBottom: 6 }}>
                                        <Checkbox
                                            label={r.group ? `${r.name} (${r.group})` : r.name}
                                            checked={selected.includes(r.uid)}
                                            onChange={() => toggle(r.uid)}
                                        />
                                        {canReceive[r.uid] === false && (
                                            <Label size="tiny" color="red" basic style={{ marginLeft: 8 }}>
                                                Can't receive notifications
                                            </Label>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </Form.Field>

                        {unreachable.length > 0 && (
                            <Message warning visible size="small">
                                {unreachable.length} {unreachable.length === 1 ? "person" : "people"} can't receive notifications (no updated App, notifications
                                turned off, or a Huawei phone). Please contact them another way for anything urgent.
                            </Message>
                        )}

                        <Button
                            color="orange"
                            type="button"
                            disabled={sending || !trimmed || selectedCount === 0}
                            loading={sending}
                            onClick={() => setConfirmOpen(true)}
                        >
                            Send Notification
                        </Button>
                    </Form>
                )}
            </Segment>

            <Segment attached>
                <Header as="h4">Sent Notifications</Header>
                {sentMessages.length === 0
                    ? "No notifications sent yet"
                    : sentMessages.map((sent) => (
                          <div key={sent.id} style={{ borderTop: "1px solid #eee", padding: "8px 0" }}>
                              <div style={{ color: "#888", fontSize: "0.9em", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <span>
                                      {sent.createdAt ? format(sent.createdAt.toDate(), "d MMM yyyy HH:mm") : "Sending..."}
                                      {sent.sentByName ? ` by ${sent.sentByName}` : ""} &middot; sent to {sent.sentUids?.length || 0} of{" "}
                                      {sent.recipientUids?.length || 0}
                                  </span>
                                  <Button basic compact size="mini" color="red" type="button" onClick={() => setDeleteTarget(sent)}>
                                      Delete
                                  </Button>
                              </div>
                              <div style={{ whiteSpace: "pre-wrap" }}>{sent.message}</div>
                              {sent.noTokenUids?.length > 0 && (
                                  <div style={{ color: "#db2828", fontSize: "0.9em" }}>Could not reach: {sent.noTokenUids.map(nameFor).join(", ")}</div>
                              )}
                          </div>
                      ))}
            </Segment>

            <Confirm
                open={deleteTarget !== null}
                size="tiny"
                header="Delete this message for everyone?"
                content="It will be removed from every traveller's Messages tab in the App. Notifications already delivered to their phones can't be recalled."
                confirmButton="Delete"
                onCancel={() => setDeleteTarget(null)}
                onConfirm={deleteSent}
            />

            <Confirm
                open={confirmOpen}
                size="tiny"
                header="Send this notification?"
                content={`This will be sent straight to ${selectedCount} ${selectedCount === 1 ? "person" : "people"} on this booking and cannot be recalled.`}
                confirmButton="Send"
                onCancel={() => setConfirmOpen(false)}
                onConfirm={send}
            />
        </>
    );
}
